/**
 * Enhanced Application Form Page
 * Multi-step application with country, role selection, and questionnaire
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import applicationService from '../services/applicationService';
import candidateService from '../services/candidateService';
import { countryService, jobRoleService, questionnaireService } from '../services/jobManagementService';

const EnhancedApplicationFormPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [candidateId, setCandidateId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic data from API
  const [countries, setCountries] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);

  const [formData, setFormData] = useState({
    country: '',
    countryId: '',
    jobRole: '',
    jobRoleId: '',
    questionnaire: {}
  });

  const totalSteps = 3;

  const loadCountries = useCallback(async () => {
    try {
      const response = await countryService.getAll(false); // Only load active countries
      if (response.success) {
        setCountries(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  }, []);

  const loadJobRoles = useCallback(async (countryId) => {
    try {
      console.log('Loading job roles for country:', countryId);
      const response = await jobRoleService.getByCountry(countryId, false); // Only load active roles
      console.log('Job roles response:', response);
      if (response.success) {
        setJobRoles(response.data || []);
        console.log('Job roles set:', response.data?.length || 0, 'roles');
      }
    } catch (err) {
      console.error('Failed to load job roles:', err);
      console.error('Error details:', err.response?.data);
      setJobRoles([]);
    }
  }, []);

  const loadQuestionnaire = useCallback(async (jobRoleId) => {
    try {
      console.log('Loading questionnaire for job role:', jobRoleId);
      const response = await questionnaireService.getByJobRole(jobRoleId);
      console.log('Questionnaire response:', response);
      if (response.success && response.data) {
        setSelectedQuestionnaire(response.data);
      } else {
        console.log('No questionnaire data in response');
        setSelectedQuestionnaire(null);
      }
    } catch (err) {
      console.error('Failed to load questionnaire:', err);
      console.error('Error details:', err.response?.data);
      setSelectedQuestionnaire(null);
    }
  }, []);

  const loadCandidateProfile = useCallback(async () => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    try {
      const response = await candidateService.getCandidateByUserId(user.id);
      if (response.success && response.data) {
        setCandidateId(response.data._id);
        setError('');
      }
    } catch (err) {
      setError('Failed to load candidate profile');
      console.error('Error loading candidate profile:', err);
    }
  }, [user]);

  useEffect(() => {
    loadCandidateProfile();
    loadCountries();
  }, [loadCandidateProfile, loadCountries]);

  useEffect(() => {
    if (formData.countryId) {
      loadJobRoles(formData.countryId);
    }
  }, [formData.countryId, loadJobRoles]);

  useEffect(() => {
    if (formData.jobRoleId) {
      loadQuestionnaire(formData.jobRoleId);
    }
  }, [formData.jobRoleId, loadQuestionnaire]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'country') {
      const selectedCountry = countries.find(c => c._id === value);
      setFormData(prev => ({
        ...prev,
        country: selectedCountry?.name || '',
        countryId: value,
        jobRole: '',
        jobRoleId: '',
        questionnaire: {}
      }));
      // Don't clear jobRoles here - let the useEffect handle loading
      setSelectedQuestionnaire(null);
    } else if (name === 'jobRole') {
      const selectedRole = jobRoles.find(r => r._id === value);
      setFormData(prev => ({
        ...prev,
        jobRole: selectedRole?.title || '',
        jobRoleId: value,
        questionnaire: {}
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleQuestionnaireChange = (questionIndex, value, type) => {
    setFormData(prev => ({
      ...prev,
      questionnaire: {
        ...prev.questionnaire,
        [questionIndex]: type === 'checkbox'
          ? (prev.questionnaire[questionIndex] || []).includes(value)
            ? (prev.questionnaire[questionIndex] || []).filter(v => v !== value)
            : [...(prev.questionnaire[questionIndex] || []), value]
          : value
      }
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.countryId) {
          setError('Please select a country');
          return false;
        }
        break;
      case 2:
        if (!formData.jobRoleId) {
          setError('Please select a job role');
          return false;
        }
        break;
      case 3:
        const questions = selectedQuestionnaire?.questions || [];
        const requiredQuestions = questions.filter(q => q.required);

        for (let i = 0; i < requiredQuestions.length; i++) {
          const question = requiredQuestions[i];
          const answer = formData.questionnaire[i];
          if (!answer || (Array.isArray(answer) && answer.length === 0) ||
            (typeof answer === 'string' && answer.trim() === '')) {
            setError(`Please answer: ${question.questionText}`);
            return false;
          }
        }
        break;
      default:
        return true;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setError('');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only allow submission on step 3
    if (currentStep !== 3) {
      handleNext();
      return;
    }

    if (!validateStep(currentStep)) {
      return;
    }

    // Prevent double submission with dedicated flag
    if (isSubmitting || loading) {
      console.warn('Submission already in progress, ignoring duplicate request');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    setError('');

    try {
      if (!candidateId) {
        setError('Unable to find your candidate profile. Please ensure your profile is complete.');
        return;
      }

      // Transform questionnaire from object to array format expected by backend
      const questions = selectedQuestionnaire?.questions || [];

      // Additional validation: ensure we have questions and answers
      if (questions.length === 0) {
        setError('No questionnaire questions found. Please try again.');
        return;
      }

      const questionnaireArray = questions.map((q, index) => ({
        question: q.questionText,
        answer: Array.isArray(formData.questionnaire[index])
          ? formData.questionnaire[index].join(', ')
          : formData.questionnaire[index] || ''
      })).filter(item => item.answer.trim() !== '');

      // Ensure we have at least some answers
      if (questionnaireArray.length === 0) {
        setError('Please answer at least one question before submitting.');
        return;
      }

      const applicationData = {
        candidateId,
        jobPosting: `${formData.jobRole} - ${formData.country}`,
        destinationCountry: formData.country,
        questionnaire: questionnaireArray
      };

      console.log('Submitting application:', applicationData);
      const response = await applicationService.createApplication(applicationData);

      if (response.success) {
        console.log('Application submitted successfully');
        navigate('/candidate/applications');
      }
    } catch (err) {
      console.error('Application submission error:', err);
      setError(err.response?.data?.error || 'Failed to create application');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex justify-between mb-6 sm:mb-8 px-2 sm:px-5">
      {[1, 2, 3].map(step => (
        <div key={step} className="flex flex-col items-center flex-1 relative">
          {step !== 3 && (
            <div className={`absolute top-4 sm:top-5 left-1/2 w-full h-0.5 -z-10 ${currentStep >= step + 1 ? 'bg-accent-green' : 'bg-border'}`} />
          )}
          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold mb-1 sm:mb-2 text-sm sm:text-base transition-all duration-300 ${currentStep > step
            ? 'bg-accent-green border-accent-green text-white'
            : currentStep === step
              ? 'border-accent-green text-accent-green bg-[rgba(107,127,92,0.1)] border-2'
              : 'bg-surface border-2 border-border text-text-tertiary'
            }`}>
            {currentStep > step ? '✓' : step}
          </div>
          <div className="text-[10px] sm:text-xs text-text-secondary font-medium text-center">
            {step === 1 && 'Country'}
            {step === 2 && 'Job Role'}
            {step === 3 && 'Questionnaire'}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="mb-4 sm:mb-6">
      <h3 className="text-lg sm:text-xl text-primary m-0 mb-1 sm:mb-2 font-semibold">Select Country</h3>
      <p className="text-xs sm:text-sm text-text-secondary m-0 mb-4 sm:mb-6">Choose the country where you want to work</p>

      <div className="mb-4 sm:mb-5">
        <label htmlFor="country" className="block mb-2 text-text-primary font-medium text-sm">Country *</label>
        <select
          id="country"
          name="country"
          value={formData.countryId}
          onChange={handleChange}
          required
          autoFocus
          className="w-full py-2.5 sm:py-3 px-3 sm:px-4 border-[1.5px] border-border rounded-lg text-sm sm:text-[15px] transition-all duration-200 bg-surface text-text-primary cursor-pointer focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
        >
          <option value="">-- Select Country --</option>
          {countries.map(country => (
            <option key={country._id} value={country._id}>
              {country.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderStep2 = () => {
    return (
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl text-primary m-0 mb-1 sm:mb-2 font-semibold">Select Job Role</h3>
        <p className="text-xs sm:text-sm text-text-secondary m-0 mb-4 sm:mb-6">Choose the position you're applying for in {formData.country}</p>

        <div className="mb-4 sm:mb-5">
          <label htmlFor="jobRole" className="block mb-2 text-text-primary font-medium text-sm">Job Role *</label>
          <select
            id="jobRole"
            name="jobRole"
            value={formData.jobRoleId}
            onChange={handleChange}
            required
            autoFocus
            className="w-full py-2.5 sm:py-3 px-3 sm:px-4 border-[1.5px] border-border rounded-lg text-sm sm:text-[15px] transition-all duration-200 bg-surface text-text-primary cursor-pointer focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
          >
            <option value="">-- Select Job Role --</option>
            {jobRoles.map(role => (
              <option key={role._id} value={role._id}>
                {role.title}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const questions = selectedQuestionnaire?.questions || [];

    if (questions.length === 0) {
      return (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl text-primary m-0 mb-1 sm:mb-2 font-semibold">Pre-Qualification Questionnaire</h3>
          <p className="text-xs sm:text-sm text-text-secondary m-0 mb-4 sm:mb-6">No questionnaire available for this role</p>
        </div>
      );
    }

    return (
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl text-primary m-0 mb-1 sm:mb-2 font-semibold">Pre-Qualification Questionnaire</h3>
        <p className="text-xs sm:text-sm text-text-secondary m-0 mb-4 sm:mb-6">Please answer the following questions for {formData.jobRole}</p>

        <div className="flex flex-col gap-4 sm:gap-6">
          {questions.map((question, index) => (
            <div key={index} className="p-3 sm:p-5 bg-background-light rounded-lg border-l-[3px] border-accent-green">
              <label className="block text-sm sm:text-[15px] font-semibold text-primary mb-2 sm:mb-3 leading-relaxed">
                {index + 1}. {question.questionText}
                {question.required && <span className="text-error ml-1">*</span>}
              </label>

              {question.fieldType === 'radio' && (
                <div className="flex flex-col gap-2 sm:gap-2.5">
                  {question.options?.map(option => (
                    <label key={option} className="flex items-center gap-2 sm:gap-2.5 p-2 sm:p-2.5 bg-white rounded-md cursor-pointer transition-all duration-200 border border-border-light hover:bg-background hover:border-accent-green">
                      <input
                        type="radio"
                        name={`question-${index}`}
                        value={option}
                        checked={formData.questionnaire[index] === option}
                        onChange={(e) => handleQuestionnaireChange(index, e.target.value, 'radio')}
                        required={question.required}
                        className="w-4 h-4 sm:w-[18px] sm:h-[18px] cursor-pointer accent-accent-green flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-text-primary break-words">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.fieldType === 'select' && (
                <select
                  value={formData.questionnaire[index] || ''}
                  onChange={(e) => handleQuestionnaireChange(index, e.target.value, 'select')}
                  required={question.required}
                  className="w-full py-2.5 sm:py-3 px-3 sm:px-4 border-[1.5px] border-border rounded-lg text-sm sm:text-[15px] transition-all duration-200 bg-surface text-text-primary cursor-pointer focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                >
                  <option value="">-- Select --</option>
                  {question.options?.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              )}

              {question.fieldType === 'checkbox' && (
                <div className="flex flex-col gap-2 sm:gap-2.5">
                  {question.options?.map(option => (
                    <label key={option} className="flex items-center gap-2 sm:gap-2.5 p-2 sm:p-2.5 bg-white rounded-md cursor-pointer transition-all duration-200 border border-border-light hover:bg-background hover:border-accent-green">
                      <input
                        type="checkbox"
                        value={option}
                        checked={(formData.questionnaire[index] || []).includes(option)}
                        onChange={(e) => handleQuestionnaireChange(index, e.target.value, 'checkbox')}
                        className="w-4 h-4 sm:w-[18px] sm:h-[18px] cursor-pointer accent-accent-green flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-text-primary break-words">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.fieldType === 'textarea' && (
                <textarea
                  value={formData.questionnaire[index] || ''}
                  onChange={(e) => handleQuestionnaireChange(index, e.target.value, 'textarea')}
                  onKeyDown={(e) => {
                    // Prevent form submission on Enter key in textarea
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.stopPropagation();
                    }
                  }}
                  placeholder={question.placeholder}
                  required={question.required}
                  rows={4}
                  className="w-full py-2.5 sm:py-3 px-3 sm:px-4 border-[1.5px] border-border rounded-lg text-xs sm:text-sm font-[inherit] resize-y transition-all duration-200 bg-white text-text-primary focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background p-3 sm:p-4 md:p-6">
      <div className="max-w-[800px] mx-auto bg-surface rounded-lg p-4 sm:p-6 md:p-8 shadow-md border border-border-light">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-[28px] text-primary m-0 mb-1 sm:mb-2 font-semibold">New Application</h1>
          <p className="text-xs sm:text-sm text-text-secondary m-0">Complete the application process in 3 simple steps</p>
        </div>

        {renderStepIndicator()}

        {error && (
          <div className="p-2.5 sm:p-3 px-3 sm:px-4 rounded-lg mb-4 sm:mb-5 text-xs sm:text-sm bg-error-bg text-error border border-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} onKeyDown={(e) => {
          // Prevent form submission on Enter key except on submit button
          if (e.key === 'Enter' && e.target.type !== 'submit' && e.target.tagName !== 'BUTTON') {
            e.preventDefault();
          }
        }} className="application-form">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t-2 border-border-light">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 border-0 rounded-lg text-sm sm:text-[15px] font-semibold cursor-pointer transition-all duration-200 bg-surface text-text-primary border-[1.5px] border-border hover:bg-background-light hover:border-accent-green disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={loading}
              >
                Previous
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/candidate/applications')}
              className="w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 border-0 rounded-lg text-sm sm:text-[15px] font-semibold cursor-pointer transition-all duration-200 bg-background text-text-secondary border-[1.5px] border-border hover:bg-background-light disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={loading}
            >
              Cancel
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                className="w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 border-0 rounded-lg text-sm sm:text-[15px] font-semibold cursor-pointer transition-all duration-200 bg-accent-green text-white hover:bg-accent-green-dark hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={loading}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 border-0 rounded-lg text-sm sm:text-[15px] font-semibold cursor-pointer transition-all duration-200 bg-accent-green text-white hover:bg-accent-green-dark hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnhancedApplicationFormPage;
